import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, FolderKanban, ChevronRight, LayoutGrid, List, ArrowUpDown, Building2, FileText, Eye, Trash2, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canSeeBudget = (project) => ['admin', 'manager'].includes(user?.role) || (user?.role === 'team_lead' && project?.project_lead === user?.user_id);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const projectLeadCandidates = useMemo(
    () => teamMembers.filter((m) => ['admin', 'manager', 'team_lead'].includes(m.role)),
    [teamMembers]
  );
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectsViewMode') || 'tile');
  const [sortField, setSortField] = useState('project_code');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active_onhold');
  const [projectLeadFilter, setProjectLeadFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    project_code: '',
    description: '',
    client_name: '',
    company_id: '',
    proposal_id: '',
    budget: '',
    start_date: '',
    end_date: '',
    project_lead: '',
    project_associate: ''
  });

  useEffect(() => {
    localStorage.setItem('projectsViewMode', viewMode);
  }, [viewMode]);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, proposalsRes, companiesRes, teamRes] = await Promise.all([
        axios.get(`${API_URL}/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/proposals`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true })
      ]);
      setProjects(projectsRes.data);
      // Show all approved/confirmed proposals - indicate which are already linked
      setProposals(proposalsRes.data.filter(p => ['approved', 'sent_to_client', 'confirmed', 'signed', 'converted'].includes(p.status)));
      setCompanies(companiesRes.data);
      setTeamMembers(teamRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle query params (new or from_proposal)
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setIsDialogOpen(true);
    }
    const fromProposalId = searchParams.get('from_proposal');
    if (!fromProposalId) return;

    // Try to find in already-loaded proposals
    const proposal = proposals.find(p => p.proposal_id === fromProposalId);
    if (proposal) {
      setFormData(prev => ({
        ...prev,
        proposal_id: fromProposalId,
        name: proposal.title || '',
        description: proposal.description || '',
        client_name: proposal.client_name || '',
        company_id: proposal.company_id || '',
        budget: proposal.amount ? String(proposal.amount) : '',
        project_code: '',
        start_date: '',
        end_date: ''
      }));
      setIsDialogOpen(true);
      setSearchParams({}, { replace: true });
    } else if (!loading) {
      // Proposals loaded but this one not in filtered list — fetch it directly
      axios.get(`${API_URL}/proposals/${fromProposalId}`, { withCredentials: true })
        .then(res => {
          const p = res.data;
          setFormData(prev => ({
            ...prev,
            proposal_id: fromProposalId,
            name: p.title || '',
            description: p.description || '',
            client_name: p.client_name || '',
            company_id: p.company_id || '',
            budget: p.amount ? String(p.amount) : '',
            project_code: '',
            start_date: '',
            end_date: ''
          }));
          setIsDialogOpen(true);
          setSearchParams({}, { replace: true });
        })
        .catch(() => {
          toast.error('Could not load proposal details');
          setSearchParams({}, { replace: true });
        });
    }
  }, [searchParams, proposals, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // When proposal is selected, auto-fill fields
  const handleProposalSelect = (proposalId) => {
    if (proposalId === '__none__') {
      setFormData(prev => ({ ...prev, proposal_id: '', name: '', description: '', client_name: '', company_id: '', budget: '' }));
      return;
    }
    const proposal = proposals.find(p => p.proposal_id === proposalId);
    if (proposal) {
      setFormData(prev => ({
        ...prev,
        proposal_id: proposalId,
        name: proposal.title || prev.name,
        description: proposal.description || prev.description,
        client_name: proposal.client_name || prev.client_name,
        company_id: proposal.company_id || prev.company_id,
        budget: proposal.amount ? String(proposal.amount) : prev.budget
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate proposal is selected
    if (!formData.proposal_id) {
      toast.error('Please select a proposal to link this project');
      return;
    }
    
    try {
      // Get company name
      let companyName = '';
      if (formData.company_id) {
        const company = companies.find(c => c.company_id === formData.company_id);
        if (company) companyName = company.name;
      }
      
      await axios.post(`${API_URL}/projects`, {
        ...formData,
        company_name: companyName,
        budget: formData.budget ? parseFloat(formData.budget) : null
      }, { withCredentials: true });
      toast.success('Project created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', project_code: '', description: '', client_name: '', company_id: '', proposal_id: '', budget: '', start_date: '', end_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleViewProject = (project) => {
    setViewingProject(project);
    setIsViewDialogOpen(true);
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      setIsViewDialogOpen(false);
      setViewingProject(null);
      setDeleteProjectId(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete project';
      toast.error(msg);
      setDeleteProjectId(null);
    }
  };

  const sortedProjects = [...projects].filter(p => {
    // Status filter
    if (statusFilter === 'active_onhold' && !['active', 'on_hold'].includes(p.status)) return false;
    if (statusFilter !== 'all' && statusFilter !== 'active_onhold' && p.status !== statusFilter) return false;
    // Project Lead filter
    if (projectLeadFilter !== 'all' && p.project_lead !== projectLeadFilter) return false;
    // Search filter
    if (projectSearch) {
      const term = projectSearch.toLowerCase();
      return (
        (p.name || '').toLowerCase().includes(term) ||
        (p.project_code || '').toLowerCase().includes(term) ||
        (p.client_name || '').toLowerCase().includes(term) ||
        (p.company_name || '').toLowerCase().includes(term)
      );
    }
    return true;
  }).sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (sortField === 'budget' || sortField === 'completion_percentage') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    }
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-chart-4/20 text-chart-4',
      planning: 'bg-yellow-500/20 text-yellow-600',
      completed: 'bg-green-500/20 text-green-600',
      on_hold: 'bg-orange-500/20 text-orange-600',
      cancelled: 'bg-red-500/20 text-red-600'
    };
    return colors[status] || 'bg-muted';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Projects</h1>
          <p className="text-base text-muted-foreground">Manage your ongoing projects</p>
        </div>
        <div className="flex gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              placeholder="Search by name, code, client, company..."
              className="pl-9"
              data-testid="project-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="project-status-filter">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active_onhold">Active & On Hold</SelectItem>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectLeadFilter} onValueChange={setProjectLeadFilter}>
            <SelectTrigger className="w-48" data-testid="project-lead-filter">
              <SelectValue placeholder="Project Lead" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Project Leads</SelectItem>
              {projectLeadCandidates.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md">
            <Button 
              variant={viewMode === 'tile' ? 'default' : 'ghost'} 
              size="sm" 
              className="rounded-r-none"
              onClick={() => setViewMode('tile')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'ghost'} 
              size="sm" 
              className="rounded-l-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-button">
                <Plus className="mr-2 h-4 w-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Link to Proposal - REQUIRED */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Link to Proposal *</label>
                  <Select value={formData.proposal_id || '__none__'} onValueChange={handleProposalSelect}>
                    <SelectTrigger className={!formData.proposal_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select a proposal to link" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select Proposal --</SelectItem>
                      {proposals.map((proposal) => (
                        <SelectItem key={proposal.proposal_id} value={proposal.proposal_id}>
                          {proposal.title} ({proposal.client_name}) - {proposal.status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Selecting a proposal will auto-fill project details</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Project Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter project name"
                    required
                    data-testid="project-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Project Code</label>
                  <Input
                    value={formData.project_code}
                    onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                    placeholder="e.g. PRJ-001"
                    data-testid="project-code-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Description *</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Project description"
                    required
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Company</label>
                    <Select value={formData.company_id || '__none__'} onValueChange={(value) => setFormData({ ...formData, company_id: value === '__none__' ? '' : value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- No Company --</SelectItem>
                        {companies.map((company) => (
                          <SelectItem key={company.company_id} value={company.company_id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Client Name</label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Client contact name"
                    />
                  </div>
                </div>
                
                {['admin', 'manager'].includes(user?.role) && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Budget (INR)</label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Start Date</label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">End Date</label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Project Lead</label>
                    <Select value={formData.project_lead || '__none__'} onValueChange={(v) => setFormData({ ...formData, project_lead: v === '__none__' ? '' : v })}>
                      <SelectTrigger data-testid="project-lead-select"><SelectValue placeholder="Select lead" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {teamMembers.map((m) => (
                          <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Project Associate</label>
                    <Select value={formData.project_associate || '__none__'} onValueChange={(v) => setFormData({ ...formData, project_associate: v === '__none__' ? '' : v })}>
                      <SelectTrigger data-testid="project-associate-select"><SelectValue placeholder="Select associate" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {teamMembers.map((m) => (
                          <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-project-button">
                    Create Project
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* View Project Dialog */}
      {viewingProject && (
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-2xl">{viewingProject.name}</DialogTitle>
                  {viewingProject.project_code && <p className="text-sm text-primary/70 mt-1">{viewingProject.project_code}</p>}
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(viewingProject.status)}`}>
                  {viewingProject.status}
                </span>
              </div>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                {viewingProject.client_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Client</label>
                    <p className="font-medium">{viewingProject.client_name}</p>
                  </div>
                )}
                {viewingProject.company_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Company</label>
                    <p className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4" />{viewingProject.company_name}</p>
                  </div>
                )}
                {canSeeBudget(viewingProject) && viewingProject.budget && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Budget</label>
                    <p className="font-medium font-mono">INR {viewingProject.budget.toLocaleString('en-IN')}</p>
                  </div>
                )}
                {viewingProject.proposal_id && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Linked Proposal</label>
                    <p className="font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {proposals.find(p => p.proposal_id === viewingProject.proposal_id)?.title || 'View Proposal'}
                    </p>
                  </div>
                )}
              </div>
              
              {viewingProject.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="font-medium">{viewingProject.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                {viewingProject.start_date && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Start Date</label>
                    <p className="font-medium">{formatDate(viewingProject.start_date)}</p>
                  </div>
                )}
                {viewingProject.end_date && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">End Date</label>
                    <p className="font-medium">{formatDate(viewingProject.end_date)}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Completion</label>
                <div className="flex items-center gap-3 mt-1">
                  <Progress value={viewingProject.completion_percentage || 0} className="flex-1" />
                  <span className="text-sm font-medium">{viewingProject.completion_percentage || 0}%</span>
                </div>
              </div>
              
              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="destructive" onClick={() => setDeleteProjectId(viewingProject.project_id)} data-testid="delete-project-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button variant="outline" onClick={() => { setIsViewDialogOpen(false); navigate(`/projects/${viewingProject.project_id}`); }}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Full Details
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {projects.length === 0 ? (
        <Card className="p-12 text-center">
          <FolderKanban className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-heading font-semibold mb-2">No projects yet</h3>
          <p className="text-muted-foreground mb-6">Create your first project to get started</p>
        </Card>
      ) : viewMode === 'list' ? (
        /* List View */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort('project_code')}>
                    <div className="flex items-center gap-2">Code <ArrowUpDown className="h-3 w-3" /></div>
                  </th>
                  <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort('name')}>
                    <div className="flex items-center gap-2">Name <ArrowUpDown className="h-3 w-3" /></div>
                  </th>
                  <th className="text-left p-4 font-medium">Client</th>
                  <th className="text-left p-4 font-medium">Company</th>
                  <th className="text-left p-4 font-medium">Lead</th>
                  <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort('status')}>
                    <div className="flex items-center gap-2">Status <ArrowUpDown className="h-3 w-3" /></div>
                  </th>
                  {['admin', 'manager', 'team_lead'].includes(user?.role) && (
                  <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort('budget')}>
                    <div className="flex items-center gap-2">Budget <ArrowUpDown className="h-3 w-3" /></div>
                  </th>
                  )}
                  <th className="text-left p-4 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort('completion_percentage')}>
                    <div className="flex items-center gap-2">Progress <ArrowUpDown className="h-3 w-3" /></div>
                  </th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => (
                  <tr 
                    key={project.project_id} 
                    className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => handleViewProject(project)}
                    data-testid={`project-row-${project.project_id}`}
                  >
                    <td className="p-4 text-sm text-primary/70">{project.project_code || '-'}</td>
                    <td className="p-4">
                      <p className="font-medium">{project.name}</p>
                    </td>
                    <td className="p-4 text-muted-foreground">{project.client_name || '-'}</td>
                    <td className="p-4 text-muted-foreground">{project.company_name || '-'}</td>
                    <td className="p-4 text-muted-foreground text-sm">{teamMembers.find(m => m.user_id === project.project_lead)?.name || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </td>
                    {['admin', 'manager', 'team_lead'].includes(user?.role) && <td className="p-4 font-mono">{canSeeBudget(project) && project.budget ? `INR ${project.budget.toLocaleString('en-IN')}` : '-'}</td>}
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Progress value={project.completion_percentage || 0} className="w-20" />
                        <span className="text-sm">{project.completion_percentage || 0}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/projects/${project.project_id}`)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* Tile View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedProjects.map((project) => (
            <Card 
              key={project.project_id} 
              className="p-6 cursor-pointer hover:shadow-md transition-shadow" 
              onClick={() => handleViewProject(project)}
              data-testid={`project-card-${project.project_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold mb-1">{project.name}</h3>
                  {project.project_code && <p className="text-xs text-primary/70 mb-1">{project.project_code}</p>}
                  {project.client_name && <p className="text-sm text-muted-foreground">{project.client_name}</p>}
                  {project.company_name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Building2 className="h-3 w-3" />{project.company_name}
                    </p>
                  )}
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{project.description}</p>
              
              {canSeeBudget(project) && project.budget && (
                <div className="mb-4 pb-4 border-b">
                  <p className="text-sm text-muted-foreground">Budget</p>
                  <p className="text-lg font-mono font-semibold">INR {project.budget.toLocaleString('en-IN')}</p>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Progress</span>
                    <span className="text-sm font-medium">{project.completion_percentage || 0}%</span>
                  </div>
                  <Progress value={project.completion_percentage || 0} />
                </div>
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.project_id}`); }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProjectId} onOpenChange={(open) => { if (!open) setDeleteProjectId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the project and all its associated tasks. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteProject(deleteProjectId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
